/**
 * The three demo personas, shared by the web demo (/demo, /demo/mitglied, …)
 * and the native app's "Demo ansehen". Both clients must show the same people
 * in the same clubs: the demo is marketing material and App Review's way in,
 * and a persona that exists on one platform only would be a second product.
 *
 * There is no platform manager among them. Owner, October 2026:
 * „Platformverwaltung wird in der Demo auf gar keinen Fall benötigt.“ The demo
 * advertises the product to clubs, and creating clubs and the
 * offboarding export are the operator's business, not theirs.
 *
 * Fictional throughout (demoSeed.guard.test.ts reads this file as text).
 * `me` is cross-tenant (ADR 0004): roles live in `memberships`, one per club,
 * and match the seed; the clients re-read them from the demo store after start.
 */
import { DEMO_TENANTS, DEMO_USERS } from './demoSeed';
import type { Me, MembershipRef } from './types';

export type DemoRole = keyof typeof DEMO_USERS;
export const DEMO_ROLES: readonly DemoRole[] = ['member', 'editor', 'admin'];

const MST = { tenantId: DEMO_TENANTS.musterstadt, name: 'Flugsportverein Musterstadt e.V.', slug: 'musterstadt', code: 'FSVM' };
const BSP = { tenantId: DEMO_TENANTS.beispieltal, name: 'Luftsportgruppe Beispieltal', slug: 'beispieltal', code: 'LSGB' };

const DEMO_PEOPLE: Record<DemoRole, Me> = {
  member: { memberId: DEMO_USERS.member, email: 'lea.demir@example.org', displayName: 'Lea Demir', isPlatformManager: false, memberships: [{ ...MST, role: 'member' }] },
  editor: {
    memberId: DEMO_USERS.editor,
    email: 'jonas.becker@example.org',
    displayName: 'Jonas Becker',
    isPlatformManager: false,
    memberships: [
      { ...MST, role: 'editor' },
      { ...BSP, role: 'admin' },
    ],
  },
  admin: { memberId: DEMO_USERS.admin, email: 'ines.albrecht@example.org', displayName: 'Ines Albrecht', isPlatformManager: false, memberships: [{ ...MST, role: 'admin' }] },
};

export function isDemoRole(value: unknown): value is DemoRole {
  return typeof value === 'string' && (DEMO_ROLES as readonly string[]).includes(value);
}

export function demoPerson(role: DemoRole): Me {
  return DEMO_PEOPLE[role];
}

function isMembership(value: unknown): value is MembershipRef {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v['tenantId'] === 'string' && typeof v['name'] === 'string' && typeof v['slug'] === 'string' && ['member', 'editor', 'admin'].includes(v['role'] as string);
}

/** Shape check of GET /api/me (ADR 0004). An old server answering with `role` instead of memberships is treated as signed out. */
export function isMe(value: unknown): value is Me {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['memberId'] === 'string' &&
    typeof v['email'] === 'string' &&
    typeof v['displayName'] === 'string' &&
    typeof v['isPlatformManager'] === 'boolean' &&
    Array.isArray(v['memberships']) &&
    v['memberships'].every(isMembership)
  );
}
